import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { trpc, type RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
} from '@org/ui';
import { useTranslation } from 'react-i18next';
import { useTeacherId } from '../components/teacher-picker';
import { FlagPill } from '../components/student-flag-badges';

type StudentItem = RouterOutputs['teacher']['students']['list']['students'][number];
type Level = StudentItem['autoFlag']['level'];
type TeacherLevel = 'red' | 'yellow' | 'green';

const LEVELS: Level[] = ['red', 'yellow', 'green', 'none'];
const TEACHER_LEVELS: TeacherLevel[] = ['red', 'yellow', 'green'];
const CATEGORIES = ['attendance', 'homework', 'community'] as const;
type Category = (typeof CATEGORIES)[number];

/** Reason categories a teacher can pick when flagging; "none" covers flags saved without one. */
const REASON_CATEGORIES = [
  'class_engagement',
  'work',
  'motivation',
  'learning_difficulty',
  'communication',
  'personal_life',
  'emotional_health',
  'family_logistics',
  'schedule_conflict',
  'other',
] as const;

const LEVEL_RANK: Record<Level, number> = { red: 0, yellow: 1, green: 2, none: 3 };

const TILE_STYLES: Record<Level, string> = {
  red: 'border-rose-200 bg-rose-50 text-rose-700',
  yellow: 'border-amber-200 bg-amber-50 text-amber-800',
  green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  none: 'border-gray-200 bg-gray-50 text-gray-600',
};

export const Route = createFileRoute('/flags')({
  component: FlagsPanelRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    teacherId: typeof search.teacherId === 'string' ? search.teacherId : undefined,
  }),
});

const emptyCounts = <K extends string>(keys: readonly K[]): Record<K, number> =>
  Object.fromEntries(keys.map((key) => [key, 0])) as Record<K, number>;

function FlagsPanelRoute() {
  const { t } = useTranslation('admin');
  const teacherId = useTeacherId();
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading } = trpc.teacher.students.list.useQuery({ teacherId });
  const allStudents = useMemo(() => data?.students ?? [], [data]);

  const groupOptions = useMemo(() => {
    const groups = new Set<string>();
    allStudents.forEach((student) => student.groups.forEach((group) => groups.add(group)));
    return Array.from(groups).sort();
  }, [allStudents]);

  const students = useMemo(
    () =>
      selectedGroup === 'all'
        ? allStudents
        : allStudents.filter((student) => student.groups.includes(selectedGroup)),
    [allStudents, selectedGroup],
  );

  const autoCounts = useMemo(() => {
    const counts = emptyCounts(LEVELS);
    students.forEach((student) => {
      counts[student.autoFlag.level] += 1;
    });
    return counts;
  }, [students]);

  const categoryCounts = useMemo(() => {
    const counts = Object.fromEntries(CATEGORIES.map((category) => [category, emptyCounts(LEVELS)])) as Record<
      Category,
      Record<Level, number>
    >;
    students.forEach((student) => {
      CATEGORIES.forEach((category) => {
        counts[category][student.autoFlag.categories[category].level] += 1;
      });
    });
    return counts;
  }, [students]);

  const teacherCounts = useMemo(() => {
    const counts = emptyCounts(TEACHER_LEVELS);
    students.forEach((student) => {
      if (student.teacherFlag) counts[student.teacherFlag.flag] += 1;
    });
    return counts;
  }, [students]);

  const reasonCounts = useMemo(() => {
    const counts = emptyCounts([...REASON_CATEGORIES, 'none'] as const);
    students.forEach((student) => {
      if (!student.teacherFlag) return;
      counts[student.teacherFlag.reason ?? 'none'] += 1;
    });
    return counts;
  }, [students]);

  const tableRows = useMemo(() => {
    const visible = showAll
      ? students
      : students.filter(
          (student) =>
            student.autoFlag.level === 'red' || student.autoFlag.level === 'yellow' || student.teacherFlag !== null,
        );
    return [...visible].sort((a, b) => {
      const byLevel = LEVEL_RANK[a.autoFlag.level] - LEVEL_RANK[b.autoFlag.level];
      if (byLevel !== 0) return byLevel;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [students, showAll]);

  const teacherFlagTotal = TEACHER_LEVELS.reduce((sum, level) => sum + teacherCounts[level], 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">{t('teacher.flags.panel.eyebrow')}</p>
          <h1 className="text-3xl font-semibold text-gray-900">{t('teacher.flags.panel.title')}</h1>
          <p className="text-sm text-gray-600">{t('teacher.flags.panel.description')}</p>
        </div>
      </div>

      {groupOptions.length > 0 && (
        <div className="flex flex-wrap gap-3 rounded-xl border border-rose-50 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-[0.2em] text-gray-500">
              {t('teacher.flags.panel.filterGroup')}
            </label>
            <Select value={selectedGroup} onValueChange={setSelectedGroup}>
              <SelectTrigger className="min-w-[200px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#cf2e2e] focus:outline-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('teacher.filters.groupAll')}</SelectItem>
                {groupOptions.map((group) => (
                  <SelectItem key={group} value={group}>
                    {group}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {isLoading && <FlagsPanelSkeleton />}

      {!isLoading && (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="border border-rose-50 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">{t('teacher.flags.panel.autoTitle')}</CardTitle>
                <CardDescription className="text-sm text-gray-600">{t('teacher.flags.panel.byLevel')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {LEVELS.map((level) => (
                    <CountTile key={level} level={level} label={t(`teacher.flags.levels.${level}`)} value={autoCounts[level]} />
                  ))}
                </div>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t('teacher.flags.panel.byCategory')}</p>
                  <div className="grid gap-2">
                    {CATEGORIES.map((category) => (
                      <div
                        key={category}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[#fff9fb] px-3 py-2 text-sm"
                      >
                        <span className="font-medium text-gray-800">{t(`teacher.flags.categories.${category}`)}</span>
                        <span className="flex flex-wrap gap-1.5">
                          {TEACHER_LEVELS.map((level) => (
                            <FlagPill
                              key={level}
                              level={level}
                              label={String(categoryCounts[category][level])}
                              title={t(`teacher.flags.levels.${level}`)}
                            />
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-rose-50 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">{t('teacher.flags.panel.teacherTitle')}</CardTitle>
                <CardDescription className="text-sm text-gray-600">{t('teacher.flags.panel.byLevel')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  {TEACHER_LEVELS.map((level) => (
                    <CountTile key={level} level={level} label={t(`teacher.flags.levels.${level}`)} value={teacherCounts[level]} />
                  ))}
                </div>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t('teacher.flags.panel.byReason')}</p>
                  {teacherFlagTotal === 0 ? (
                    <p className="text-sm text-gray-500">{t('teacher.flags.historyEmpty')}</p>
                  ) : (
                    <ul className="grid gap-1 text-sm">
                      {[...REASON_CATEGORIES, 'none' as const]
                        .filter((reason) => reasonCounts[reason] > 0)
                        .map((reason) => (
                          <li key={reason} className="flex items-center justify-between gap-2 rounded-xl bg-[#fff9fb] px-3 py-1.5">
                            <span className="text-gray-800">
                              {reason === 'none' ? t('teacher.flags.noReason') : t(`teacher.flags.reasonCategories.${reason}`)}
                            </span>
                            <span className="font-semibold text-gray-900">{reasonCounts[reason]}</span>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border border-rose-50 bg-white shadow-sm">
            <CardHeader className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-xl text-gray-900">{t('teacher.flags.panel.studentsTitle')}</CardTitle>
                <Badge variant="outline" className="rounded-full border-rose-200 bg-white px-4 py-1 text-[#cf2e2e]">
                  {t('teacher.flags.panel.studentsCount', { count: tableRows.length })}
                </Badge>
              </div>
              <label className="flex w-fit cursor-pointer items-center gap-3 text-sm text-gray-700">
                <Switch checked={showAll} onCheckedChange={setShowAll} className="data-[state=checked]:bg-[#cf2e2e]" />
                {t('teacher.flags.panel.showAll')}
              </label>
            </CardHeader>
            <CardContent>
              {tableRows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-rose-200 p-6 text-center text-sm text-gray-500">
                  {t('teacher.flags.panel.empty')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[960px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-rose-100 text-xs uppercase tracking-[0.15em] text-gray-500">
                        <th className="px-3 py-2 font-medium">{t('teacher.flags.panel.columns.student')}</th>
                        <th className="px-3 py-2 font-medium">{t('teacher.flags.panel.columns.group')}</th>
                        <th className="px-3 py-2 font-medium">{t('teacher.flags.panel.columns.auto')}</th>
                        <th className="px-3 py-2 font-medium">{t('teacher.flags.panel.columns.attendance')}</th>
                        <th className="px-3 py-2 font-medium">{t('teacher.flags.panel.columns.homework')}</th>
                        <th className="px-3 py-2 font-medium">{t('teacher.flags.panel.columns.community')}</th>
                        <th className="px-3 py-2 font-medium">{t('teacher.flags.panel.columns.teacher')}</th>
                        <th className="px-3 py-2 font-medium">{t('teacher.flags.panel.columns.reason')}</th>
                        <th className="px-3 py-2 font-medium">{t('teacher.flags.panel.columns.updated')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((student) => (
                        <StudentRow key={student.studentId} student={student} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function CountTile({ level, label, value }: { level: Level; label: string; value: number }) {
  return (
    <div className={`rounded-xl border px-3 py-2 ${TILE_STYLES[level]}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] opacity-80">{label}</p>
      <p className="text-2xl font-semibold">{value}</p>
    </div>
  );
}

function StudentRow({ student }: { student: StudentItem }) {
  const { t } = useTranslation('admin');
  const teacherFlag = student.teacherFlag;
  return (
    <tr className="border-b border-rose-50 align-top last:border-b-0 hover:bg-rose-50/40">
      <td className="px-3 py-2">
        <Link
          to="/students/$studentId"
          params={{ studentId: student.studentId }}
          search={{ teacherId: undefined }}
          className="font-semibold text-gray-900 hover:text-[#cf2e2e] hover:underline"
        >
          {student.displayName}
        </Link>
      </td>
      <td className="px-3 py-2 text-gray-700">{student.groups[0] ?? t('teacher.students.noGroup')}</td>
      <td className="px-3 py-2">
        <FlagPill level={student.autoFlag.level} />
      </td>
      {CATEGORIES.map((category) => (
        <td key={category} className="px-3 py-2">
          <FlagPill level={student.autoFlag.categories[category].level} />
        </td>
      ))}
      <td className="px-3 py-2">
        {teacherFlag ? <FlagPill level={teacherFlag.flag} /> : <span className="text-gray-400">{'—'}</span>}
      </td>
      <td className="px-3 py-2 text-gray-700">
        {teacherFlag
          ? teacherFlag.reason
            ? t(`teacher.flags.reasonCategories.${teacherFlag.reason}`)
            : t('teacher.flags.noReason')
          : ''}
      </td>
      <td className="px-3 py-2 text-gray-700">
        {teacherFlag ? new Date(teacherFlag.createdAt).toLocaleDateString() : ''}
      </td>
    </tr>
  );
}

function FlagsPanelSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, index) => (
          <Card key={`flags-skeleton-${index}`} className="border border-rose-50">
            <CardHeader className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="border border-rose-50">
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    </div>
  );
}
